<template>
  <div>
    <section>
      <b-row>
        <b-col md="2">
          <label for="search-filter"> {{ $t('generals.search') }}: </label>
          <b-form-input
            id="search-filter"
            v-model="query_params.search"
            @input="delayedFilter"
          />
        </b-col>
        <b-col md="2">
          <label for="brand-filter"> {{ $t('contract.brand') }} </label>
          <v-select
            v-model="query_params.brand"
            :options="GET_CONSTRUCTORS"
            label="title"
            :reduce="(brand) => brand.id"
            multiple
            @input="changePage(1)"
          />
        </b-col>
        <b-col md="2">
          <label for="object-filter"> {{ $t('contract.residence') }} </label>
          <v-select
            v-model="query_params.objects"
            :options="GET_OBJECTS"
            label="title"
            :reduce="(object) => object.id"
            multiple
            @input="changePage(1)"
          />
        </b-col>
        <b-col md="2">
          <label for="payment-type-filter">
            {{ $t('contract.payment_type') }}
          </label>
          <v-select
            v-model="query_params.payment_type"
            :options="GET_PAYMENT_TYPES"
            :get-option-label="(opt) => opt.title[$i18n.locale]"
            multiple
            :reduce="(type) => type.id"
            @input="changePage(1)"
          />
        </b-col>
        <b-col md="2">
          <label for="payment-type-filter">
            {{ $t('contract.payment_status') }}
          </label>
          <v-select
            v-model="query_params.paid"
            :options="payment_status_options"
            :reduce="(status) => status.value"
            @input="changePage(1)"
          />
        </b-col>

        <template
          v-if="
            !isSigned &&
              !waitingList &&
              checkPermission('add-unsigned-contract')
          "
        >
          <b-col
            v-if="GET_USER_PERMISSIONS.includes('content_management')"
            md="2"
            class="d-flex justify-content-end"
          >
            <b-dropdown
              variant="primary"
              class="mt-auto w-100 vertical-align-end"
              :text="$t('generals.add')"
              right
              menu-class="w-100"
            >
              <b-dropdown-item @click="$router.push({ name: 'new-contract' })">
                <feather-icon icon="FileIcon" />
                {{ $t('contract.new_contract') }}
              </b-dropdown-item>
              <b-dropdown-item @click="$router.push({ name: 'old-contract' })">
                <feather-icon icon="FileIcon" />
                {{ $t('contract.old_contract') }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col v-else class="d-flex justify-content-end" md="2">
            <b-button
              variant="primary"
              class="mt-auto vertical-align-end"
              @click="$router.push({ name: 'new-contract' })"
            >
              <feather-icon icon="PlusIcon" />
              {{ $t('generals.add') }}
            </b-button>
          </b-col>
        </template>
      </b-row>  
    </section>
    <section v-if="checkPermission('list-unsigned-contract')">
      <b-table
        id="contracts-table"
        :fields="fields"
        :items="GET_CONTRACTS.results"
        :tbody-tr-class="['tableRowClass']"
        show-empty
        small
      >
        <template #empty>
          <div class="text-center">
            {{ $t('general.no_records_to_show') }}
          </div>
        </template>
        <template #cell(contract_number)="{ item }">
          <span class="cursor-pointer" @click="viewContract(item)">
            {{ item.contract_number }}
          </span>
        </template>
        <template #head()="{ label }">
          {{ $t(label) }}
        </template>
        <template #cell(qr_code)="{ item }">
          <b-img
            class="w-100 cursor-pointer"
            :src="item.qr"
            @click="showQrCode(item.qr)"
          />
        </template>
        <template #cell(is_payed)="{ item }">
          <p :class="item.is_payed ? 'text-success' : 'text-danger'">
            {{ $t(item.is_payed ? 'paid' : 'not_paid') }}
          </p>
        </template>
        <template #cell(actions)="{ item }">
          <template v-if="!waitingList">
            <b-button
              v-if="notVisibleForManagers(item.action === 'edit')"
              size="sm"
              class="px-50"
              variant="primary"
              @click="
                $router.push({ name: 'contract-edit', params: { id: item.id } })
              "
            >
              <feather-icon size="18" icon="EditIcon" />
            </b-button>
            <b-button
              v-else-if="item.action === 'wait'"
              size="sm"
              class="px-50"
              variant="primary"
            >
              <feather-icon icon="BellIcon" />
            </b-button>
            <b-button
              v-else-if="notVisibleForManagers(!isSigned)"
              class="px-50"
              :title="$t('contract.request_to_edit')"
              @click="requestToEdit(item.id)"
            >
              {{ $t('contract.request') }}
            </b-button>
            <b-button
              v-if="VisibleForManagers(!isSigned)"
              v-b-modal.contract-sign-modal
              size="sm"
              class="px-50"
              variant="warning"
              @click="contract_id_to_sign = item.id"
            >
              <feather-icon size="18" icon="FilePlusIcon" />
            </b-button>
          </template>
          <template v-else-if="waitingList">
            <b-button
              v-if="notVisibleForManagers(item.action === 'approve')"
              class="px-50"
              variant="info"
              @click="approveEditing(item.id)"
            >
              {{ $t('generals.approved') }}
            </b-button>
            <b-button
              v-if="notVisibleForManagers()"
              class="px-50"
              variant="primary"
              @click="approveRequest(item.id)"
            >
              <feather-icon icon="CheckIcon" />
            </b-button>
            <b-button
              v-if="notVisibleForManagers()"
              class="px-50"
              @click="rejectRequest(item.id)"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </template>
          <b-button
            size="sm"
            class="px-50"
            variant="danger"
            @click="downloadPdf(item)"
          >
            <feather-icon size="18" icon="FileIcon" />
          </b-button>
        </template>
      </b-table>
      <div
        class="d-flex justify-content-between align-items-center flex-wrap mb-75 bg-white p-1"
      >
        <div class="d-flex align-items-center mb-0">
          <span class="text-nowrap text-black ml-1">
            {{ $t('generals.show_from') }}
          </span>
          <b-form-select
            v-model="query_params.page_size"
            :options="['5', '10', '15']"
            class="mx-1 text-black"
            @change="refresh"
          />
          <!-- <span class="text-nowrap text-black"> {{$t('pagination.entries')}} </span> -->
        </div>
        <div class="mb-1">
          <b-pagination
            v-model="query_params.page"
            :total-rows="GET_CONTRACTS.count"
            :per-page="query_params.page_size"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @change="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </div>
    </section>
    <sign-up-contract
      v-if="!isSigned"
      :contract_id="contract_id_to_sign"
      @success="refresh"
    />
    <qr-code-modal :photo="selected_qrcode" />
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BRow,
  BCol,
  BFormInput,
  BFormSelect,
  BPagination,
  BImg,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import VSelect from 'vue-select'
import { mapActions, mapGetters } from 'vuex'
import download from '@/mixins/download'
import signUpContract from './components/signUpContractModal.vue'
import qrCodeModal from './components/qrCodeModal.vue'
import checkPermission from '@/mixins/checkPermission'
/* eslint-disable */
export default {
  components: {
    BTable,
    // BCard,
    BButton,
    FeatherIcon,
    BRow,
    BCol,
    BFormInput,
    VSelect,
    BFormSelect,
    BPagination,
    BImg,
    signUpContract,
    qrCodeModal,
    BDropdown,
    BDropdownItem,
  },
  mixins: [download, checkPermission],
  props: {
    isSigned: {
      type: Boolean,
      required: true,
    },
    waitingList: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      query_params: {
        page_size: 10,
        page: 1,
        is_actual: true,
      },
      fields: [
        {
          key: 'qr_code',
          label: 'contract.qr_code',
          thClass: 'tableHeadClass text-center',
          tdClass: 'width-100',
        },
        {
          key: 'contract_number',
          label: 'contract.contract_number',
          thClass: 'tableHeadClass',
        },
        {
          key: 'date',
          label: 'objects.date',
          thClass: 'tableHeadClass text-center',
          tdClass: 'text-center',
          formatter(val) {
            return new Date(val).toLocaleString().split(',')[0]
          },
        },
        {
          key: 'customer.name',
          label: 'contract.investor_name',
          thClass: 'tableHeadClass',
        },
        {
          key: 'author',
          label: 'clients.manager',
          thClass: 'tableHeadClass',
        },
        {
          key: 'brand',
          label: 'contract.brand',
          thClass: 'tableHeadClass',
          formatter(val) {
            return val.title
          },
        },
        {
          key: 'is_payed',
          label: 'is_payed',
          thClass: 'tableHeadClass',
        },
        {
          key: 'rc_object',
          label: 'contract.residence',
          thClass: 'tableHeadClass',
        },
        {
          key: 'actions',
          label: 'kanban.actions',
          tdClass: 'width-150 d-flex justify-content-around',
          thClass: 'tableHeadClass',
        },
        // {
        //   key: 'phones',
        //   label: 'Тел номеры',
        // },
        // {
        //   key: 'receipt',
        //   label: 'Квитанция',
        // },
        // {
        //   key: 'payment',
        //   label: 'Оплата',
        // },
      ],
      contract_id_to_sign: '',
      payment_status_options: [
        {
          label: this.$t('contract.paid'),
          value: true,
        },
        {
          label: this.$t('contract.unpaid'),
          value: false,
        },
      ],
      delay: 300,
      selected_qrcode: '',
    }
  },
  computed: {
    ...mapGetters('contracts', ['GET_CONTRACTS']),
    ...mapGetters('knowledgeBase', ['GET_CONSTRUCTORS']),
    ...mapGetters('users', ['GET_USER_PERMISSIONS']),
    ...mapGetters('objects', ['GET_OBJECTS', 'GET_PAYMENT_TYPES']),
  },
  watch: {
    isSigned() {
      this.query_params.page = 1
      this.refresh()
    },
    waitingList() {
      this.query_params.page = 1
      this.refresh()
    },
  },
  mounted() {
    this.refresh()
    this.FETCH_CONSTRUCTORS()
    this.FETCH_OBJECTS({ partial: true })
    this.FETCH_PAYMENT_TYPES()
  },
  methods: {
    ...mapActions('contracts', ['FETCH_CONTRACTS', 'EDIT_CONTRACT']),
    ...mapActions('knowledgeBase', ['FETCH_CONSTRUCTORS']),
    ...mapActions('objects', ['FETCH_OBJECTS', 'FETCH_PAYMENT_TYPES']),
    refresh() {
      this.$bvModal.hide('contract-sign-modal')
      this.query_params.sign_in = this.isSigned
      this.query_params.action = this.waitingList ? 'wait' : null
      this.FETCH_CONTRACTS(this.query_params)
    },
    downloadPdf(item) {
      console.log(item)
      // if (item.sign_in) {
      //   this.$_downloadInterval(item.files)
      // } else {
      this.$_downloadBlob(item.file)
      // }
      // this.$_downloadInterval(url)
    },
    notVisibleForManagers(value) {
      let a = false
      if (this.GET_USER_PERMISSIONS.includes('admin')) {
        a = true
      } else if (this.GET_USER_PERMISSIONS.includes('supervisor')) {
        a = true
      } else if (this.GET_USER_PERMISSIONS.includes('sales_management')) {
        a = false
      }
      return a && value
    },
    VisibleForManagers(value) {
      let a = false
      if (this.GET_USER_PERMISSIONS.includes('admin')) {
        a = true
      } else if (this.GET_USER_PERMISSIONS.includes('supervisor')) {
        a = true
      } else if (this.GET_USER_PERMISSIONS.includes('sales_management')) {
        a = true
      } else if (this.GET_USER_PERMISSIONS.includes('manager')) {
        a = true
      }
      return a && value
    },
    delayedFilter() {
      clearTimeout(this.delay)
      this.delay = setTimeout(() => {
        this.query_params.page = 1
        this.refresh()
      }, 300)
    },
    changePage(page = 1) {
      this.query_params.page = page
      this.refresh()
    },
    showQrCode(photo) {
      this.selected_qrcode = photo
      this.$bvModal.show('qr-code-modal')
    },
    requestToEdit(contract_id) {
      this.EDIT_CONTRACT({ id: contract_id, action: 'wait' })
        .then(() => {
          this.$_successToast(this.$t('contract.sent_request_to_edit'))
          this.refresh()
        })
        .catch(() => {
          this.$_errorToast(this.$t('generals.error'))
        })
    },
    rejectRequest(contract_id) {
      this.EDIT_CONTRACT({ id: contract_id, action: 'request' })
        .then(() => {
          this.$_successToast(this.$t('contract.request_rejected'))
          this.refresh()
        })
        .catch(() => {
          this.$_errorToast(this.$t('generals.error'))
        })
    },
    approveRequest(contract_id) {
      this.$_showCustomModal(
        this.$t('clients.attention'),
        this.$t('contract.are_you_really_want_to_approve_request'),
      ).then((res) => {
        if (res) {
          this.EDIT_CONTRACT({ id: contract_id, action: 'edit' })
            .then(() => {
              this.$_successToast(this.$t('contract.request_approved'))
              this.refresh()
            })
            .catch(() => {
              this.$_errorToast(this.$t('generals.error'))
            })
        }
      })
    },
    viewContract(params) {
      this.$router.push({ name: 'contract-view', params: { id: params.id } })
    },
    approveEditing(contract_id) {
      this.$_showCustomModal(
        this.$t('clients.attention'),
        this.$t('contract.are_you_really_want_to_approve_edit'),
      ).then((res) => {
        if (res) {
          this.EDIT_CONTRACT({ id: contract_id, action: 'request' })
            .then(() => {
              this.$_successToast(this.$t('clients.approved'))
              this.refresh()
            })
            .catch(() => {
              this.$_errorToast(this.$t('generals.error'))
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
#contracts-table {
  border-collapse: separate;
  border-spacing: 0 1rem;
  .tableRowClass {
    background: white;
    transform: translateY(5px);
    transition: all 0.5s ease-in-out;
    // text-align: center;
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
    // -webkit-box-shadow:  0 4px 20px 0px lightgrey;  /* Safari 3-4, iOS 4.0.2 - 4.2, Android 2.3+ */
    // -moz-box-shadow:     0 4px 20px 0px lightgrey;  /* Firefox 3.5 - 3.6 */
    // box-shadow:          0 4px 6px 0px lightgrey;
    td {
      border-top: 0;
      padding: 20px !important;
      &:first-child {
        // padding-left: 25px !important;
        border-top-left-radius: 0.5rem;
        border-bottom-left-radius: 0.5rem;
      }

      &:last-child {
        border-top-right-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
      }
    }
  }

  .tableRowClass:hover {
    transform: translateY(0);
    // -webkit-box-shadow:  0 4px 20px 0px lightgrey;  /* Safari 3-4, iOS 4.0.2 - 4.2, Android 2.3+ */
    // -moz-box-shadow:     0 4px 20px 0px lightgrey;  /* Firefox 3.5 - 3.6 */
    // box-shadow:          0 4px 6px 0px lightgrey;
  }

  .tableHeadClass {
    background: none !important;
    border: none !important;
    // text-align: center;
    // padding-left: 25px !important;
  }
  .tableRowClass:hover {
    .tableTitle {
      color: #7367f0 !important;
    }
  }
}
</style>
